import { HttpClient } from '@angular/common/http';
import { map, Observable, of, tap } from 'rxjs';
import { FieldExpression, NodeExpression, term, toString as luceneToString } from 'lucene';
import { Injectable } from '@angular/core';

import {
	DropdownFilter,
	FilterApiRequest,
	SuggestiveListFilter,
	DocumentMillDropdownFilter,
	DocumentSuggestiveListFilterWithId,
	SuggestiveListWithIdFilter
} from '../models/datacenter-filters';
import { PaginatedResult } from '../models/paginated-result';

import { UserService } from './user.service';

interface DatacenterFilterApiItem {
	code: string | undefined;
	translation: string;
	extraField?: string | undefined;
}

interface DatacenterDocumentSuggestiveApiItem {
	id: string | undefined;
	code: string | undefined;
}

@Injectable({ providedIn: 'any' })
export class DatacenterFilterApiService {
	private readonly suggestiveCache = new Map<string, { code: string | undefined; translation: string }[]>();
	private readonly documentCache = new Map<string, { code?: string | undefined }[]>();
	private readonly cache = new Map<
		string,
		DatacenterFilterApiItem[]
	>();

	private readonly documentSuggestiveCache = new Map<string, DatacenterDocumentSuggestiveApiItem[]>();

	constructor(
		private readonly userService: UserService,
		private readonly http: HttpClient
	) {}

	getItemsByCodes(
		filter: DocumentSuggestiveListFilterWithId,
		codes: string[]
	): Observable<DatacenterDocumentSuggestiveApiItem[]>;
	getItemsByCodes(
		filter:
			| SuggestiveListFilter
			| SuggestiveListWithIdFilter
			| DropdownFilter
			| DocumentMillDropdownFilter
			| undefined,
		codes: string[]
	): Observable<DatacenterFilterApiItem[]>;
	getItemsByCodes(
		filter:
			| SuggestiveListFilter
			| SuggestiveListWithIdFilter
			| DropdownFilter
			| DocumentMillDropdownFilter
			| DocumentSuggestiveListFilterWithId
			| undefined,
		codes: string[]
	): Observable<DatacenterFilterApiItem[] | DatacenterDocumentSuggestiveApiItem[]> {
		if (!filter) {
			return of([]);
		}
		if (filter instanceof DocumentSuggestiveListFilterWithId) {
			if (!filter.apiCode || !filter.apiNameCode) {
				return of([]);
			}
			const { requestApi: docRequestApi } = filter;
			const uniqueDocCodes = Array.from(new Set(codes.filter(Boolean)));
			if (uniqueDocCodes.length === 0) {
				return of([]);
			}

			const { user: docUser } = this.userService;
			const docUrl = new URL(docRequestApi.url);
			const docParams = docUrl.searchParams;

			docParams.set('fields', [filter.apiCode, filter.apiNameCode].join(','));
			docParams.set('visibility', String(docUser.currentVisibility.id));
			docParams.set('orderBy', filter.apiNameCode);
			docParams.set('distinct', String(docRequestApi.distinct));
			docParams.set('limit', Math.max(docRequestApi.limit, uniqueDocCodes.length).toString());

			const docFilterFields = this.getFilterFieldsForCodesRequest(filter, filter.apiCode);
			const docFilterExpression = this.buildOrExpressionForFields(docFilterFields, uniqueDocCodes);
			docParams.set('filter', luceneToString(docFilterExpression));

			const cachedDocResult = this.documentSuggestiveCache.get(docUrl.toString());
			if (cachedDocResult) {
				return of(cachedDocResult);
			}

			return this.http
				.get<PaginatedResult<Record<string, unknown>> | Record<string, unknown>[]>(docUrl.toString())
				.pipe(
					map(values => {
						const array = Array.isArray(values) ? values : values.results;
						return array.map(
							(value): DatacenterDocumentSuggestiveApiItem => ({
								id: filter.apiCode ? this.getPropertyFromObject(value, filter.apiCode) : undefined,
								code: filter.apiNameCode
									? this.getPropertyFromObject(value, filter.apiNameCode)
									: undefined
							})
						);
					}),
					tap(result => {
						this.documentSuggestiveCache.set(docUrl.toString(), result);
					})
				);
		}
		const { requestApi } = filter;
		const { apiNameCode, apiNameTranslation } = filter;
		if (!apiNameCode || !apiNameTranslation) {
			return of([]);
		}
		const uniqueCodes = Array.from(new Set(codes.filter(Boolean)));
		if (uniqueCodes.length === 0) {
			return of([]);
		}

		const { user } = this.userService;
		const url = new URL(requestApi.url);
		const params = url.searchParams;

		const extraFieldsRaw = filter instanceof SuggestiveListWithIdFilter ? filter.extraFields : undefined;
		const extraFieldNames = (extraFieldsRaw ?? '')
			.split(',')
			.map(field => field.trim())
			.filter(Boolean);
		const [primaryExtraField] = extraFieldNames;

		const fields = [apiNameCode, apiNameTranslation, ...extraFieldNames]
			.map(field => field.trim())
			.filter(Boolean);
		params.set('fields', Array.from(new Set(fields)).join(','));
		params.set('visibility', String(user.currentVisibility.id));
		params.set('orderBy', apiNameCode);
		params.set('distinct', String(requestApi.distinct));
		params.set('limit', Math.max(requestApi.limit, uniqueCodes.length).toString());

		const filterFields = this.getFilterFieldsForCodesRequest(filter, apiNameCode);
		const filterExpression = this.buildOrExpressionForFields(filterFields, uniqueCodes);
		params.set('filter', luceneToString(filterExpression));

		const cacheResult = this.cache.get(url.toString());
		if (cacheResult) {
			return of(cacheResult);
		}

		return this.http.get<PaginatedResult<Record<string, unknown>> | Record<string, unknown>[]>(url.toString())
			.pipe(
				map(values => {
					const array = Array.isArray(values) ? values : values.results;
					return array.map(
						(value): DatacenterFilterApiItem => ({
							code: apiNameCode ? this.getPropertyFromObject(value, apiNameCode) : undefined,
							translation: this.getPropertyFromObject(value, apiNameTranslation),
							extraField: primaryExtraField
								? this.getPropertyFromObject(value, primaryExtraField)
								: undefined
						})
					);
				}),
				tap(result => {
					this.cache.set(url.toString(), result);
				})
			);
	}

	getItemBySearchText(filter: SuggestiveListFilter | DropdownFilter | undefined, searchText: string) {
		if (!filter) {
			return of([]);
		}
		const filterValue = searchText ? {
			field: filter.apiNameTranslation,
			term: `*${ term.escape(searchText) }*`,
			quoted: false
		} : undefined;

		return this.sendRequest(filter, filterValue);
	}

	getItemBySearchTextOrId(
		filter: SuggestiveListWithIdFilter | undefined,
		searchText: string,
		extraText: string | undefined,
		searchId: string | undefined
	) {
		if (!filter) {
			return of([]);
		}
		if (searchText) {
			const filterDescription: FieldExpression = {
				field: filter.apiNameTranslation,
				term: `*${ term.escape(searchText) }*`,
				quoted: false
			};

			const filterCode: FieldExpression = {
				field: filter.apiNameCode,
				term: searchId ?? `*${ term.escape(searchText) }*`,
				quoted: false
			};

			let filterConditionNode: NodeExpression = {
				left: filterDescription,
				operator: 'OR',
				right: filterCode,
				parenthesized: true
			};
			if (extraText && filter.extraFields) {
				const extraFieldNames = filter.extraFields
					.split(',')
					.map(field => field.trim())
					.filter(Boolean);

				for (const extraFieldName of extraFieldNames) {
					const filterExtraText: FieldExpression = {
						field: extraFieldName,
						term: `*${ term.escape(extraText) }*`,
						quoted: false
					};
					filterConditionNode = {
						left: filterConditionNode,
						operator: 'AND',
						right: filterExtraText,
						parenthesized: false
					};
				}
			}
			return this.sendRequestWithTextAndID(filter, filterConditionNode);
		}
		return this.sendRequestWithTextAndID(filter, undefined);
	}

	getDocumentBySearchTextOrId(
		filter: DocumentSuggestiveListFilterWithId | undefined,
		query: string,
		queryId?: string
	): Observable<DatacenterDocumentSuggestiveApiItem[]> {
		if (!filter) {
			return of([]);
		}
		if (query) {
			const filterValue = {
				field: filter.apiNameCode,
				term: `*${ term.escape(query) }*`,
				quoted: false
			};
			const filterCode = {
				field: filter.apiCode,
				term: queryId ?? `*${ term.escape(query) }*`,
				quoted: false
			};

			const filterConditionNode: NodeExpression = {
				left: filterValue,
				operator: 'OR',
				right: filterCode,
				parenthesized: true
			};
			return this.sendDocumentRequestWithId(filter, filterConditionNode);
		}

		return this.sendDocumentRequestWithId(filter, undefined);
	}

	sendDocumentDropdownRequest(filter: DocumentMillDropdownFilter | undefined): Observable<DatacenterFilterApiItem[]> {
		if (!filter) {
			return of([]);
		}

		const { requestApi, apiNameCode, apiNameTranslation } = filter;
		const url = new URL(requestApi.url);
		const cacheResult = this.cache.get(url.toString());
		if (cacheResult) {
			return of(cacheResult);
		}

		return this.http.get<PaginatedResult<Record<string, string>>>(url.toString())
			.pipe(
				map(
					paginatedResult =>
						paginatedResult.results.map(value => ({
							code: apiNameCode ? this.getPropertyFromObject(value, apiNameCode) : undefined,
							translation: this.getPropertyFromObject(value, apiNameTranslation),
						}))
				),
				tap(result => {
					this.cache.set(url.toString(), result);
				})
			);
	}

	sendRequest(filter: SuggestiveListFilter | DropdownFilter | undefined, filterVal?: FieldExpression) {
		if (!filter) {
			return of([]);
		}

		const { user } = this.userService;
		const { requestApi, apiNameCode, apiNameTranslation } = filter;
		const url = new URL(requestApi.url);
		const params = url.searchParams;

		params.append('fields', apiNameCode
			? `${ apiNameCode }, ${ apiNameTranslation }`
			: apiNameTranslation);

		params.append('visibility', String(user.currentVisibility.id));

		// eslint-disable-next-line unicorn/prefer-logical-operator-over-ternary
		params.append('orderBy', apiNameCode
			? apiNameCode
			: apiNameTranslation);

		if (filterVal) {
			params.append('filter', luceneToString(filterVal));
		}

		this.setParameters(params, requestApi);

		const cacheResult = this.cache.get(url.toString());
		if (cacheResult) {
			return of(cacheResult);
		}

		return this.http.get<PaginatedResult<Record<string, string>> | Record<string, string>[]>(url.toString())
			.pipe(
				map(
					values => {
						const array = 'results' in values ? values.results : values;
						return array.map(value => ({
							code: apiNameCode ? this.getPropertyFromObject(value, apiNameCode) : undefined,
							translation: this.getPropertyFromObject(value, apiNameTranslation),
						}));
					}
				),
				tap(result => {
					this.cache.set(url.toString(), result);
				})
			);
	}

	private getPropertyFromObject(value: Record<string, unknown>, apiName: string) {
		const [firstElement, secondElement] = apiName.split('.');
		if (secondElement) {
			return (<Record<string, string>> value[firstElement])[secondElement];
		}
		return <string> value[firstElement];
	}

	private getFilterFieldsForCodesRequest(
		filter:
			| SuggestiveListFilter
			| SuggestiveListWithIdFilter
			| DropdownFilter
			| DocumentMillDropdownFilter
			| DocumentSuggestiveListFilterWithId,
		apiNameCode: string
	) {
		if (!(filter instanceof DocumentSuggestiveListFilterWithId)) {
			return [apiNameCode];
		}
		if (!filter.entityName) {
			return [apiNameCode];
		}
		if (filter.entityName.includes('.')) {
			return [filter.entityName];
		}
		return [apiNameCode, `${ filter.entityName }.${ apiNameCode }`];
	}

	private buildOrExpressionForFields(fields: string[], codes: string[]): FieldExpression | NodeExpression {
		const filteredFields = fields
			.map(field => field.trim())
			.filter(Boolean);
		if (filteredFields.length === 0) {
			return this.buildOrExpression('<implicit>', codes);
		}
		const [firstField, ...restFields] = filteredFields;
		let expression: FieldExpression | NodeExpression = this.buildOrExpression(firstField, codes);
		for (const field of restFields) {
			expression = {
				left: expression,
				operator: 'OR',
				parenthesized: true,
				right: this.buildOrExpression(field, codes)
			};
		}
		return expression;
	}

	private buildOrExpression(field: string, codes: string[]): FieldExpression | NodeExpression {
		const [firstCode, ...restCodes] = codes;
		let expression: FieldExpression | NodeExpression = {
			field,
			term: term.escape(firstCode),
			quoted: false
		};
		for (const code of restCodes) {
			const fieldExpression: FieldExpression = {
				field,
				term: term.escape(code),
				quoted: false
			};
			expression = {
				left: expression,
				operator: 'OR',
				parenthesized: false,
				right: fieldExpression
			};
		}
		return expression;
	}

	private setParameters(searchParams: URLSearchParams, apiParams: FilterApiRequest) {
		searchParams.append('distinct', String(apiParams.distinct));
		searchParams.append('limit', apiParams.limit.toString());
	}

	private sendRequestWithTextAndID(
		filter: SuggestiveListWithIdFilter | undefined,
		filterVal: NodeExpression | undefined
	) {
		if (!filter) {
			return of([]);
		}

		const { user } = this.userService;
		const { requestApi, apiNameCode, apiNameTranslation, extraFields } = filter;
		const url = new URL(requestApi.url);
		const params = url.searchParams;

		let fieldsValue = apiNameCode
			? `${ apiNameCode },${ apiNameTranslation }`
			: apiNameTranslation;

		if (extraFields) {
			fieldsValue += `,${ extraFields }`;
		}

		params.append('fields', fieldsValue);
		params.append('visibility', String(user.currentVisibility.id));

		params.append('orderBy', apiNameTranslation);

		if (filterVal) {
			params.append('filter', luceneToString(filterVal));
		}

		this.setParameters(params, requestApi);

		const suggestiveCacheResult = this.suggestiveCache.get(url.toString());
		if (suggestiveCacheResult) {
			return of(suggestiveCacheResult);
		}

		return this.http.get<PaginatedResult<Record<string, string>>>(url.toString())
			.pipe(
				map(
					paginatedResult =>
						paginatedResult.results.map(value => ({
							code: apiNameCode ? this.getPropertyFromObject(value, apiNameCode) : undefined,
							translation: this.getPropertyFromObject(value, apiNameTranslation),
							extraField: extraFields ? this.getPropertyFromObject(value, extraFields) : undefined
						}))
				),
				tap(result => {
					this.cache.set(url.toString(), result);
				})
			);
	}

	private sendDocumentRequestWithId(
		filter: DocumentSuggestiveListFilterWithId | undefined,
		filterValue: NodeExpression | undefined
	) {
		if (!filter) {
			return of([]);
		}

		const { user } = this.userService;
		const { requestApi, apiCode, apiNameCode } = filter;
		const url = new URL(requestApi.url);
		const params = url.searchParams;

		params.append('fields', apiNameCode
			? `${ apiNameCode },${ apiCode }`
			: apiCode);
		params.append('visibility', String(user.currentVisibility.id));
		params.append('orderBy', apiNameCode);

		if (filterValue) {
			params.append('filter', luceneToString(filterValue));
		}

		this.setParameters(params, requestApi);

		const documentSuggestiveCacheResult = this.documentSuggestiveCache.get(url.toString());
		if (documentSuggestiveCacheResult) {
			return of(documentSuggestiveCacheResult);
		}

		return this.http.get<PaginatedResult<Record<string, string>>>(url.toString())
			.pipe(
				map(
					paginatedResult =>
						paginatedResult.results.map(value => ({
							id: apiCode ? this.getPropertyFromObject(value, apiCode) : undefined,
							code: apiNameCode ? this.getPropertyFromObject(value, apiNameCode) : undefined
						}))
				),
				tap(result => {
					this.documentCache.set(url.toString(), result);
				})
			);
	}
}
